import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  Observable,
  catchError,
  finalize,
  map,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { AuthTokenService } from '../Services/auth-token.service';
import { environment } from '../../../../environments/environment';
import { endpoints } from '../../../../environments/endpoints';
import { AuthResponse } from '../../../Interfaces/api/auth/auth-response.interface';
import { LaravelDefaultResponse } from '../../../Interfaces/api/shared/response.interface';

let refreshRequest$: Observable<AuthResponse> | null = null;

function redirectToLogin(authTokenService: AuthTokenService, router: Router): void {
  authTokenService.setReturnUrl(router.url);
  authTokenService.clearToken();
  router.navigate(['/auth/login']);
}

function refreshAccessToken(
  http: HttpClient,
  authTokenService: AuthTokenService,
): Observable<AuthResponse> {
  if (!refreshRequest$) {
    const refresh_token = authTokenService.getRefreshToken();

    if (!refresh_token) {
      return throwError(() => new Error('No refresh token available.'));
    }

    refreshRequest$ = http
      .post<LaravelDefaultResponse<AuthResponse>>(
        `${environment.api.host}/${endpoints.auth.refresh}`,
        { refresh_token },
        { headers: { 'skip-loading': 'true' } },
      )
      .pipe(
        map((response) => response.data),
        tap((auth_data) => authTokenService.setToken(auth_data)),
        finalize(() => {
          refreshRequest$ = null;
        }),
        shareReplay(1),
      );
  }

  return refreshRequest$;
}

export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const authTokenService = inject(AuthTokenService);
  const router = inject(Router);
  const http = new HttpClient(inject(HttpBackend));

  const access_token = authTokenService.getToken();

  if (access_token && !authTokenService.isTokenExpired()) {
    req = req.clone({ setHeaders: { Authorization: `Bearer ${access_token}` } });
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const is_unauthorized = error.status === 401;
      const is_refresh_call = req.url.includes(endpoints.auth.refresh);
      const is_logout_call = req.url.includes(endpoints.auth.logout);

      if (!is_unauthorized || is_logout_call) {
        return throwError(() => error);
      }

      // Impersonated session

      if (authTokenService.isImpersonated()) {
        redirectToLogin(authTokenService, router);

        return throwError(() => error);
      }

      if (is_refresh_call || !authTokenService.hasRefreshToken()) {
        redirectToLogin(authTokenService, router);

        return throwError(() => error);
      }

      return refreshAccessToken(http, authTokenService).pipe(
        switchMap((auth_data) =>
          next(req.clone({ setHeaders: { Authorization: `Bearer ${auth_data.access_token}` } })),
        ),
        catchError((refresh_error) => {
          redirectToLogin(authTokenService, router);

          return throwError(() => refresh_error);
        }),
      );
    }),
  );
};
